import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { ScrollText, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import * as auditApi from '../../api/audit.api';
import { errorMessage } from '../../api/client';

// Super-admin audit log viewer. Filter by module/action/date range, paged.
// Action list is fetched per module so the dropdown stays relevant.

function AuditLogs() {
  const [data, setData] = useState({ items: [], total: 0, totalPages: 1, modules: [] });
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    module: '', action: '', from: '', to: '', page: 1, pageSize: 50,
  });
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) params[k] = v; });
      const fresh = await auditApi.listAuditLogs(params);
      setData(fresh || { items: [] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Refresh action list whenever the module filter changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await auditApi.listAuditActions(filters.module ? { module: filters.module } : {});
        if (!cancelled) setActions(list || []);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [filters.module]);

  function setFilter(k, v) {
    setFilters((s) => ({ ...s, [k]: v, page: 1 }));
  }

  function gotoPage(delta) {
    setFilters((s) => ({ ...s, page: Math.max(1, Math.min(data.totalPages, s.page + delta)) }));
  }

  function clearFilters() {
    setFilters({ module: '', action: '', from: '', to: '', page: 1, pageSize: 50 });
  }

  return (
    <>
      <PageHeader
        title="Audit logs"
        subtitle="Security-sensitive activity across auth, orders, payments, clinics, progress and reviews."
        actions={
          <button className="btn btn-outline" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        }
      />

      <div className="card" style={{ marginBottom: 'var(--s-3)' }}>
        <div className="grid grid-4" style={{ gap: 'var(--s-3)' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Module</label>
            <select className="input" value={filters.module} onChange={(e) => setFilter('module', e.target.value)}>
              <option value="">All modules</option>
              {(data.modules || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Action</label>
            <select className="input" value={filters.action} onChange={(e) => setFilter('action', e.target.value)}>
              <option value="">All actions</option>
              {actions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>From</label>
            <input type="date" className="input" value={filters.from} onChange={(e) => setFilter('from', e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>To</label>
            <input type="date" className="input" value={filters.to} onChange={(e) => setFilter('to', e.target.value)} />
          </div>
        </div>
        <div className="row" style={{ gap: 8, marginTop: 'var(--s-3)' }}>
          <button className="btn btn-link" onClick={clearFilters}>Clear filters</button>
          <span className="muted text-sm">
            {data.total ? `${data.total.toLocaleString()} entries` : '—'}
          </span>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : !data.items.length ? (
        <EmptyState
          icon={ScrollText}
          title="No audit entries"
          description="Try widening the date range or clearing filters."
        />
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Module</th>
                <th>Action</th>
                <th>User</th>
                <th>Role</th>
                <th>IP</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <React.Fragment key={r._id}>
                  <tr>
                    <td className="cell-mono text-xs">{new Date(r.createdAt).toLocaleString()}</td>
                    <td><span className="badge">{r.module}</span></td>
                    <td className="cell-mono">{r.action}</td>
                    <td>
                      {r.user ? (
                        <>
                          <div>{r.user.name}</div>
                          <div className="muted text-xs">{r.user.email}</div>
                        </>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td>{r.role || r.user?.role || '—'}</td>
                    <td className="muted text-xs">{r.ipAddress || '—'}</td>
                    <td>
                      <button
                        className="btn btn-link"
                        onClick={() => setExpanded(expanded === r._id ? null : r._id)}
                      >
                        {expanded === r._id ? 'Hide' : 'Details'}
                      </button>
                    </td>
                  </tr>
                  {expanded === r._id && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--ink-100)' }}>
                        <div className="cell-mono text-xs" style={{ whiteSpace: 'pre-wrap' }}>
                          <strong>entityId:</strong> {r.entityId || '—'}{'\n'}
                          <strong>userAgent:</strong> {r.userAgent || '—'}{'\n'}
                          <strong>metadata:</strong>{'\n'}
                          {JSON.stringify(r.metadata || {}, null, 2)}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="row between" style={{ marginTop: 'var(--s-3)', alignItems: 'center' }}>
            <div className="muted text-sm">
              Page {data.page || filters.page} of {data.totalPages || 1}
            </div>
            <div className="row" style={{ gap: 6 }}>
              <button className="btn btn-outline" onClick={() => gotoPage(-1)} disabled={(data.page || 1) <= 1}>
                <ChevronLeft size={14} /> Prev
              </button>
              <button className="btn btn-outline" onClick={() => gotoPage(+1)} disabled={(data.page || 1) >= (data.totalPages || 1)}>
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AuditLogs;
